import { createSlice } from '@reduxjs/toolkit';
import { cookie } from '../../utils/cookie';

const initialState = {
  hasConsented: false,
  showBanner: true,
  consentSettings: {
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  },
  consentTimestamp: null,
  isLoaded: false
};

const consentSlice = createSlice({
  name: 'consent',
  initialState,
  reducers: {
    loadConsentFromStorage: (state) => {
      const savedConsent = cookie.get('cookieConsent');
      if (savedConsent) {
        state.hasConsented = true;
        state.showBanner = false;
        state.consentSettings = {
          necessary: savedConsent.necessary || true,
          analytics: savedConsent.analytics || false,
          marketing: savedConsent.marketing || false,
          preferences: savedConsent.preferences || false
        };
        state.consentTimestamp = savedConsent.timestamp;
      }
      state.isLoaded = true;
    },

    updateConsent: (state, action) => {
      const { consentSettings, timestamp } = action.payload;

      state.hasConsented = true;
      state.showBanner = false;
      state.consentSettings = {
        necessary: true, // Always true
        ...consentSettings
      };
      state.consentTimestamp = timestamp;

      // Save to cookie (use setWithDomain for cross-subdomain consent)
      const consentData = {
        ...state.consentSettings,
        timestamp: timestamp
      };
      cookie.setWithDomain('cookieConsent', consentData, { expire: 365 * 24 * 3600 }); // 1 year
    },

    showConsentBanner: (state) => {
      state.showBanner = true;
    },

    hideConsentBanner: (state) => {
      state.showBanner = false;
    },

    acceptAllConsent: (state) => {
      const timestamp = new Date().toISOString();
      const fullConsent = {
        necessary: true,
        analytics: true,
        marketing: true,
        preferences: true
      };

      state.hasConsented = true;
      state.showBanner = false;
      state.consentSettings = fullConsent;
      state.consentTimestamp = timestamp;

      // Save to cookie (use setWithDomain for cross-subdomain consent)
      const consentData = { ...fullConsent, timestamp };
      cookie.setWithDomain('cookieConsent', consentData, { expire: 365 * 24 * 3600 });
    },

    rejectAllConsent: (state) => {
      const timestamp = new Date().toISOString();
      const minimalConsent = {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false
      };

      state.hasConsented = true;
      state.showBanner = false;
      state.consentSettings = minimalConsent;
      state.consentTimestamp = timestamp;

      // Save to cookie (use setWithDomain for cross-subdomain consent)
      const consentData = { ...minimalConsent, timestamp };
      cookie.setWithDomain('cookieConsent', consentData, { expire: 365 * 24 * 3600 });
    },

    resetConsent: (state) => {
      state.hasConsented = false;
      state.showBanner = true;
      state.consentSettings = {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false
      };
      state.consentTimestamp = null;

      // Remove cookie from all domains
      cookie.eraseAll('cookieConsent');
    },

    updateConsentSettings: (state, action) => {
      const { type, value } = action.payload;

      if (type !== 'necessary') { // Can't modify necessary cookies
        state.consentSettings[type] = value;
      }
    }
  }
});

export const {
  loadConsentFromStorage,
  updateConsent,
  showConsentBanner,
  hideConsentBanner,
  acceptAllConsent,
  rejectAllConsent,
  resetConsent,
  updateConsentSettings
} = consentSlice.actions;

// Selectors
export const selectConsentState = (state) => state.consent;
export const selectHasConsented = (state) => state.consent.hasConsented;
export const selectShowBanner = (state) => state.consent.showBanner;
export const selectConsentSettings = (state) => state.consent.consentSettings;
export const selectAnalyticsConsent = (state) => state.consent.consentSettings.analytics;
export const selectMarketingConsent = (state) => state.consent.consentSettings.marketing;
export const selectPreferencesConsent = (state) => state.consent.consentSettings.preferences;
export const selectIsConsentLoaded = (state) => state.consent.isLoaded;

export default consentSlice.reducer;